import { useEffect, useState } from "react";
import { PageHeader, Panel } from "../components/Surface";
import { Button } from "../components/ui/Button";
import {
  getToday, setDutyStatus, submitDvir, listDvirs, exportRods,
  DUTY_STATUSES, DUTY_LABELS, VIOLATION_LABELS,
  type HosSummary, type DutyStatus, type DutyEventRow, type Dvir,
} from "../lib/logbook";

function Clock({ label, hours, danger }: { label: string; hours: number; danger?: boolean }) {
  const v = Math.max(0, hours);
  return (
    <div className="grid gap-1 rounded-lg border border-line p-4 text-center">
      <span className={`text-2xl font-extrabold tabular-nums ${danger ? "text-danger" : ""}`}>{v.toFixed(1)}h</span>
      <span className="font-mono text-[11px] text-muted">{label}</span>
    </div>
  );
}

export function Logbook() {
  const [sum, setSum] = useState<HosSummary | null>(null);
  const [events, setEvents] = useState<DutyEventRow[]>([]);
  const [dvirs, setDvirs] = useState<Dvir[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  async function refresh() {
    const [today, dv] = await Promise.all([getToday(), listDvirs()]);
    setSum(today.summary); setEvents(today.events); setDvirs(dv.dvirs);
  }
  useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

  async function change(status: DutyStatus) {
    setBusy(true); setNote(null);
    try { await setDutyStatus({ status }); await refresh(); } finally { setBusy(false); }
  }

  async function dvir(kind: "pre_trip" | "post_trip") {
    setBusy(true);
    try { await submitDvir({ kind, safe_to_operate: true }); setNote(`${kind === "pre_trip" ? "Pre-trip" : "Post-trip"} DVIR logged.`); await refresh(); }
    finally { setBusy(false); }
  }

  async function download() {
    const { csv } = await exportRods();
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "rods-log.csv"; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="mx-auto grid w-full max-w-3xl gap-6">
      <PageHeader
        title="Log book"
        subtitle="Hours-of-Service duty status, clocks, and DVIR. A companion record — not a certified ELD."
        actions={<Button variant="ghost" onClick={download} data-testid="export">Export RODS (CSV)</Button>}
      />

      {loading ? (
        <p className="font-mono text-sm text-muted">Loading…</p>
      ) : !sum ? (
        <p className="font-mono text-sm text-danger">Couldn't load your log.</p>
      ) : (
        <>
          <Panel title="Duty status">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="duty-buttons">
              {DUTY_STATUSES.map((s) => (
                <Button key={s} variant={sum.current_status === s ? "amber" : "ghost"} disabled={busy}
                  data-testid={`duty-${s}`} onClick={() => change(s)}>
                  {DUTY_LABELS[s]}
                </Button>
              ))}
            </div>
          </Panel>

          {sum.violations.length > 0 && (
            <div className="rounded-lg border border-danger p-3" data-testid="violations">
              {sum.violations.map((v) => (
                <p key={v} className="font-mono text-[12px] font-bold text-danger">⚠️ {VIOLATION_LABELS[v] ?? v}</p>
              ))}
            </div>
          )}
          {sum.break_needed && sum.violations.length === 0 && (
            <p className="font-mono text-[12px] text-[#c9a227]" data-testid="break">☕ 30-minute break due.</p>
          )}

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="hos-clocks">
            <Clock label="Drive left (11h)" hours={sum.drive_remaining_hours} danger={sum.violations.includes("drive_limit")} />
            <Clock label="Window left (14h)" hours={sum.window_remaining_hours} danger={sum.violations.includes("window_limit")} />
            <Clock label="Cycle left (70h)" hours={sum.cycle_remaining_hours} danger={sum.violations.includes("cycle_limit")} />
            <Clock label="Driven today" hours={sum.drive_hours} />
          </div>

          <Panel title="Vehicle inspection (DVIR)">
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="ghost" disabled={busy} onClick={() => dvir("pre_trip")} data-testid="dvir-pre">Log pre-trip</Button>
              <Button variant="ghost" disabled={busy} onClick={() => dvir("post_trip")} data-testid="dvir-post">Log post-trip</Button>
              {note && <span className="font-mono text-[11px] text-verified" data-testid="logbook-note">{note}</span>}
            </div>
            {dvirs.length > 0 && (
              <ul className="mt-3 grid gap-1.5" data-testid="dvir-list">
                {dvirs.slice(0, 8).map((d) => (
                  <li key={d.id} className="flex items-center justify-between border-b border-line pb-1.5 text-sm last:border-0 last:pb-0">
                    <span className="font-mono text-[11px] text-muted">{new Date(d.created_at).toLocaleString()} · {d.kind === "pre_trip" ? "Pre-trip" : "Post-trip"}</span>
                    <span className={`font-mono text-[11px] font-bold ${d.safe_to_operate ? "text-verified" : "text-danger"}`}>{d.safe_to_operate ? "Safe" : "Defect"}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel title="Today's log">
            {events.length === 0 ? (
              <p className="font-mono text-sm text-muted">No duty changes logged today.</p>
            ) : (
              <ul className="grid gap-1.5" data-testid="event-list">
                {events.map((e, i) => (
                  <li key={i} className="flex items-center justify-between border-b border-line pb-1.5 text-sm last:border-0 last:pb-0">
                    <span className="font-bold">{DUTY_LABELS[e.status]}</span>
                    <span className="font-mono text-[11px] text-muted">
                      {new Date(e.started_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      {e.location ? ` · ${e.location}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
